<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import SalesDetailFactList from './SalesDetailFactList.vue';
import SalesDetailCommissionAllocationTable from './SalesDetailCommissionAllocationTable.vue';

const props = defineProps({
  group: { type: Object, required: true },
  installments: { type: Array, default: () => [] },
  summary: { type: Boolean, default: false },
});
const { t } = useI18n();
const optionistReportLabels = computed(() => ({
  simples: t('SALES.DETAIL.COMMISSION.OPTIONIST_REPORT.SIMPLES'),
  com_chave: t('SALES.DETAIL.COMMISSION.OPTIONIST_REPORT.COM_CHAVE'),
  exclusividade: t('SALES.DETAIL.COMMISSION.OPTIONIST_REPORT.EXCLUSIVIDADE'),
  lançamento: t('SALES.DETAIL.COMMISSION.OPTIONIST_REPORT.LANCAMENTO'),
  lancamento: t('SALES.DETAIL.COMMISSION.OPTIONIST_REPORT.LANCAMENTO'),
}));
const optionistReportPercentages = {
  simples: 16,
  com_chave: 18,
  exclusividade: 20,
  lançamento: 8,
  lancamento: 8,
};
const summaryTitles = computed(() => ({
  gerentes_gerais: {
    singular: t(
      'SALES.DETAIL.COMMISSION.ALLOCATIONS.SUMMARY.GENERAL_MANAGER_SINGULAR'
    ),
    plural: t(
      'SALES.DETAIL.COMMISSION.ALLOCATIONS.SUMMARY.GENERAL_MANAGER_PLURAL'
    ),
    count: t(
      'SALES.DETAIL.COMMISSION.ALLOCATIONS.SUMMARY.GENERAL_MANAGER_COUNT'
    ),
  },
  gerentes: {
    singular: t('SALES.DETAIL.COMMISSION.ALLOCATIONS.SUMMARY.MANAGER_SINGULAR'),
    plural: t('SALES.DETAIL.COMMISSION.ALLOCATIONS.SUMMARY.MANAGER_PLURAL'),
    count: t('SALES.DETAIL.COMMISSION.ALLOCATIONS.SUMMARY.MANAGER_COUNT'),
  },
  corretores_vendedores: {
    singular: t(
      'SALES.DETAIL.COMMISSION.ALLOCATIONS.SUMMARY.SELLING_BROKER_SINGULAR'
    ),
    plural: t(
      'SALES.DETAIL.COMMISSION.ALLOCATIONS.SUMMARY.SELLING_BROKER_PLURAL'
    ),
    count: t(
      'SALES.DETAIL.COMMISSION.ALLOCATIONS.SUMMARY.SELLING_BROKER_COUNT'
    ),
  },
  corretores_opcionistas: {
    singular: t(
      'SALES.DETAIL.COMMISSION.ALLOCATIONS.SUMMARY.OPTIONIST_BROKER_SINGULAR'
    ),
    plural: t(
      'SALES.DETAIL.COMMISSION.ALLOCATIONS.SUMMARY.OPTIONIST_BROKER_PLURAL'
    ),
    count: t(
      'SALES.DETAIL.COMMISSION.ALLOCATIONS.SUMMARY.OPTIONIST_BROKER_COUNT'
    ),
  },
}));
const recipients = computed(
  () => props.installments[0]?.[props.group.key] || []
);
const firstRecipient = computed(() => recipients.value[0] || {});
const metadata = computed(() => summaryTitles.value[props.group.key]);
const summaryTitle = computed(() =>
  recipients.value.length > 1 ? metadata.value.plural : metadata.value.singular
);
const percentage = value => {
  if (value === null || value === undefined || value === '')
    return t('SALES.DETAIL.EMPTY_VALUE');

  return String(value).endsWith('%') ? value : `${value}%`;
};
const reportLabel = installment => {
  if (props.group.key !== 'corretores_opcionistas') return null;

  return optionistReportLabels.value[
    installment[props.group.key][0]?.tipo_laudo_opcionista
  ];
};
const reportType = computed(() => firstRecipient.value.tipo_laudo_opcionista);
const summaryItems = computed(() => {
  const total =
    props.group.key === 'corretores_opcionistas'
      ? optionistReportPercentages[reportType.value]
      : firstRecipient.value.total_comissao ||
        firstRecipient.value.total_comissao_corretor;
  const items = [];
  if (props.group.key === 'corretores_opcionistas') {
    items.push({
      key: 'report-type',
      label: t('SALES.DETAIL.COMMISSION.ALLOCATIONS.SUMMARY.REPORT_TYPE'),
      value:
        optionistReportLabels.value[reportType.value] ||
        t('SALES.DETAIL.EMPTY_VALUE'),
    });
  }
  items.push(
    {
      key: 'total',
      label: t('SALES.DETAIL.COMMISSION.ALLOCATIONS.SUMMARY.TOTAL_COMMISSION'),
      value: percentage(total),
    },
    {
      key: 'count',
      label: metadata.value.count,
      value: recipients.value.length,
    }
  );
  return items;
});
</script>

<template>
  <LaisCard
    :title="summary ? summaryTitle : group.label"
    prefix-icon="i-lucide-percent"
  >
    <SalesDetailFactList v-if="summary" :items="summaryItems" />
    <section
      v-for="installment in installments"
      :key="installment.id"
      class="border-b border-lais-border-soft py-4 first:pt-0 last:border-b-0 last:pb-0"
    >
      <div v-if="!summary" class="mb-3 flex flex-wrap items-center gap-2">
        <span
          class="rounded-full bg-lais-primary-50 px-2 py-1 text-xs font-bold text-lais-primary"
        >
          {{
            t('SALES.DETAIL.COMMISSION.ALLOCATIONS.INSTALLMENT', {
              number: installment.numero_parcela,
            })
          }}
        </span>
        <span
          v-if="installment[group.key][0]?.total_comissao"
          class="rounded-full bg-emerald-100 px-2 py-1 text-xs font-bold text-emerald-800"
        >
          {{
            t('SALES.DETAIL.COMMISSION.ALLOCATIONS.TOTAL', {
              value: installment[group.key][0].total_comissao,
            })
          }}
        </span>
        <span
          v-if="reportLabel(installment)"
          class="rounded-full bg-lais-bg-soft px-2 py-1 text-xs font-bold text-lais-text-muted"
        >
          {{ reportLabel(installment) }}
        </span>
      </div>
      <SalesDetailCommissionAllocationTable
        :recipients="installment[group.key]"
        :summary="summary"
      />
    </section>
  </LaisCard>
</template>
